import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Check, X, Upload, Link as LinkIcon, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common spelling corrections for Portuguese (accents)
const spellingSuggestions = {
  'agua': 'água', 'aguas': 'águas', 'alem': 'além', 'arvore': 'árvore',
  'cafe': 'café', 'ceu': 'céu', 'coracao': 'coração', 'crianca': 'criança',
  'dificil': 'difícil', 'esperanca': 'esperança', 'familia': 'família',
  'historia': 'história', 'historias': 'histórias', 'impossivel': 'impossível',
  'infancia': 'infância', 'justica': 'justiça', 'lagrima': 'lágrima',
  'mae': 'mãe', 'magica': 'mágica', 'musica': 'música', 'obstaculo': 'obstáculo',
  'pais': 'país', 'passaro': 'pássaro', 'possivel': 'possível',
  'reflexao': 'reflexão', 'silencio': 'silêncio', 'tambem': 'também',
  'unico': 'único', 'visao': 'visão',
};

function checkSpelling(text) {
  if (!text) return null;
  const words = text.toLowerCase().split(/\s+/);
  let correctedText = text;
  let hasCorrections = false;
  
  words.forEach(word => {
    const cleanWord = word.replace(/[.,!?;:'"]/g, '');
    if (spellingSuggestions[cleanWord]) {
      const regex = new RegExp(`\\b${cleanWord}\\b`, 'gi');
      const original = text.match(regex);
      if (original) {
        original.forEach(match => {
          let replacement = spellingSuggestions[cleanWord];
          if (match[0] === match[0].toUpperCase()) {
            replacement = replacement.charAt(0).toUpperCase() + replacement.slice(1);
          }
          correctedText = correctedText.replace(match, replacement);
          hasCorrections = true;
        });
      }
    }
  });
  
  return hasCorrections ? correctedText : null;
}

// Book format specifications with dimensions
const bookFormats = {
  pocket: {
    label: 'Pocket',
    dimensions: '12 x 18 cm',
    wordsPerPage: 250,
    avgPages: 160,
    avgWords: 40000,
    description: 'Ideal para novelas e livros compactos (~250 palavras/página)'
  },
  kindle: {
    label: 'Kindle / Padrão',
    dimensions: '14 x 21 cm',
    wordsPerPage: 280,
    avgPages: 250,
    avgWords: 70000,
    description: 'Formato padrão para romances e não-ficção (~280 palavras/página)'
  }
};

// Editor page color options
const pageColors = [
  { id: 'white', label: 'Branco', color: '#ffffff', textColor: '#1a1a1a' },
  { id: 'beige', label: 'Bege suave', color: '#f5f0e6', textColor: '#3d3d3d' },
  { id: 'dark', label: 'Escuro', color: '#1a1a1a', textColor: '#e5e5e5' }
];

export default function NovoLivro() {
  const navigate = useNavigate();
  const { bookId } = useParams(); // For editing existing books
  const [loading, setLoading] = useState(false);
  const [loadingBook, setLoadingBook] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [titleSuggestion, setTitleSuggestion] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [spellCheckEnabled] = useState(() => {
    const saved = localStorage.getItem('spellCheckEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    authors: [''], // Array for multiple authors
    size: 'kindle',
    cover_url: '',
    editor_page_color: 'white'
  });

  // Load book function (declared before useEffect that depends on it)
  const loadBook = useCallback(async () => {
    if (!bookId) return;
    setLoadingBook(true);
    try {
      const response = await axios.get(`${API_URL}/api/books/${bookId}`, { withCredentials: true });
      const book = response.data;
      
      // Parse authors - could be comma-separated or single author
      const authorList = book.author ? book.author.split(',').map(a => a.trim()) : [''];
      
      setFormData({
        title: book.title || '',
        subtitle: book.subtitle || '',
        authors: authorList.length > 0 ? authorList : [''],
        size: book.size || 'kindle',
        cover_url: book.cover_url || '',
        editor_page_color: localStorage.getItem(`book_${bookId}_pageColor`) || 'white'
      });
      setIsEditMode(true);
    } catch (error) {
      console.error('Error loading book:', error);
      toast.error('Erro ao carregar livro');
      navigate('/meus-livros');
    } finally {
      setLoadingBook(false);
    }
  }, [bookId, navigate]);

  // Load existing book for editing
  useEffect(() => {
    if (bookId) {
      loadBook();
    }
  }, [bookId, loadBook]);

  // Check spelling when title changes
  useEffect(() => {
    if (spellCheckEnabled && formData.title) {
      const suggestion = checkSpelling(formData.title);
      setTitleSuggestion(suggestion);
    } else {
      setTitleSuggestion(null);
    }
  }, [formData.title, spellCheckEnabled]);

  const handleApplySuggestion = () => {
    if (titleSuggestion) {
      setFormData({ ...formData, title: titleSuggestion });
      setTitleSuggestion(null);
      toast.success('Correção aplicada');
    }
  };

  // Author management functions
  const handleAddAuthor = () => {
    setFormData({ ...formData, authors: [...formData.authors, ''] });
  };

  const handleRemoveAuthor = (index) => {
    if (formData.authors.length > 1) {
      const newAuthors = formData.authors.filter((_, i) => i !== index);
      setFormData({ ...formData, authors: newAuthors });
    }
  };

  const handleAuthorChange = (index, value) => {
    const newAuthors = [...formData.authors];
    newAuthors[index] = value;
    setFormData({ ...formData, authors: newAuthors });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const validAuthors = formData.authors.filter(a => a.trim());
    if (!formData.title || validAuthors.length === 0) {
      toast.error('Título e pelo menos um autor são obrigatórios');
      return;
    }

    setLoading(true);
    
    // Show creation animation only for new books
    if (!isEditMode) {
      setShowAnimation(true);
      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    try {
      const bookData = {
        title: formData.title,
        subtitle: formData.subtitle,
        author: validAuthors.join(', '), // Join authors with comma
        size: formData.size,
        cover_url: formData.cover_url
      };

      let bookIdToUse = bookId;

      if (isEditMode) {
        // Update existing book
        await axios.put(
          `${API_URL}/api/books/${bookId}`,
          bookData,
          { withCredentials: true }
        );
        toast.success('Livro atualizado com sucesso!');
      } else {
        // Create new book
        const response = await axios.post(
          `${API_URL}/api/books`,
          bookData,
          { withCredentials: true }
        );
        bookIdToUse = response.data.book_id;
        
        // Create first chapter for new books
        await axios.post(
          `${API_URL}/api/books/${bookIdToUse}/chapters`,
          { title: 'Capítulo 1', content: '', order: 1 },
          { withCredentials: true }
        );
      }
      
      // Save editor page color preference
      localStorage.setItem(`book_${bookIdToUse}_pageColor`, formData.editor_page_color);
      
      setShowAnimation(false);
      navigate(`/livro/${bookIdToUse}`);
    } catch (error) {
      console.error('Error saving book:', error);
      setShowAnimation(false);
      toast.error(isEditMode ? 'Erro ao atualizar livro.' : 'Erro ao criar livro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const format = bookFormats[formData.size];

  // Loading state for edit mode
  if (loadingBook) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
          <div className="max-w-3xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-16 w-16 mx-auto rounded-full bg-muted"></div>
              <div className="h-10 bg-muted rounded w-1/2 mx-auto"></div>
              <div className="h-32 bg-muted rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Creation Animation Modal
  if (showAnimation) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
        <div className="text-center max-w-md px-8">
          {/* Animated Book */}
          <div className="relative w-32 h-40 mx-auto mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* Book spine */}
              <div className="w-4 h-36 bg-amber-600 rounded-l-sm"></div>
              {/* Book cover - animating open */}
              <div 
                className="w-24 h-36 bg-gradient-to-r from-amber-700 to-amber-600 rounded-r-md origin-left animate-pulse"
                style={{
                  boxShadow: '2px 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                <div className="h-full flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-amber-200 animate-bounce" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Inspirational Message */}
          <p className="text-2xl font-serif text-foreground mb-4 animate-fade-in">
            "Alguém em algum lugar precisa da sua história."
          </p>
          
          <p className="text-muted-foreground animate-pulse">
            Preparando seu livro...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-16 md:pt-8" data-testid="novo-livro-page">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif mb-2">
              {isEditMode ? 'Editar Livro' : 'Meu Novo Livro'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode ? 'Atualize as informações do seu livro' : 'O início de uma nova jornada literária'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Section: Identidade do Livro */}
            <div className="bg-card border border-border/40 rounded-lg p-6">
              <h2 className="text-xl font-serif mb-6 pb-2 border-b border-border/40">Identidade do Livro</h2>
              
              {/* Título */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Título *</label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="O título da sua obra"
                  required
                  className="text-lg"
                  data-testid="book-title-input"
                />
                
                {/* Spelling Suggestion */}
                {titleSuggestion && (
                  <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">
                      Sugestão: <span className="font-medium text-foreground">{titleSuggestion}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleApplySuggestion}
                        className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                      >
                        <Check size={14} className="mr-1" />
                        Aplicar correção
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setTitleSuggestion(null)}
                      >
                        <X size={14} className="mr-1" />
                        Manter como está
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Subtítulo */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Subtítulo (opcional)</label>
                <Input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Um complemento ao título, se desejar"
                  data-testid="book-subtitle-input"
                />
              </div>

              {/* Autor(es) - Multiple authors support */}
              <div>
                <label className="block text-sm font-medium mb-2">Autor(es) *</label>
                <div className="space-y-2">
                  {formData.authors.map((author, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="text"
                        value={author}
                        onChange={(e) => handleAuthorChange(index, e.target.value)}
                        placeholder={index === 0 ? "Seu nome ou pseudônimo" : "Nome do coautor"}
                        required={index === 0}
                        data-testid={`book-author-input-${index}`}
                      />
                      {formData.authors.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAuthor(index)}
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={18} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleAddAuthor}
                  className="mt-2 text-amber-600 hover:text-amber-700"
                >
                  <Plus size={16} className="mr-1" />
                  Adicionar coautor
                </Button>
              </div>
            </div>

            {/* Section: Formato e Aparência */}
            <div className="bg-card border border-border/40 rounded-lg p-6">
              <h2 className="text-xl font-serif mb-6 pb-2 border-b border-border/40">Formato e Aparência</h2>
              
              {/* Tamanho do Livro */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Formato do Livro</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, size: 'pocket' })}
                    className={`
                      p-4 border rounded-lg text-center transition-all
                      ${formData.size === 'pocket' 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-border hover:border-amber-500/50'
                      }
                    `}
                    data-testid="size-pocket"
                  >
                    <div className="w-8 h-12 mx-auto mb-2 border-2 border-current rounded bg-card"></div>
                    <p className="font-medium">{bookFormats.pocket.label}</p>
                    <p className="text-sm text-amber-600 font-medium">{bookFormats.pocket.dimensions}</p>
                    <p className="text-xs text-muted-foreground mt-1">{bookFormats.pocket.description}</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, size: 'kindle' })}
                    className={`
                      p-4 border rounded-lg text-center transition-all
                      ${formData.size === 'kindle' 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-border hover:border-amber-500/50'
                      }
                    `}
                    data-testid="size-kindle"
                  >
                    <div className="w-10 h-14 mx-auto mb-2 border-2 border-current rounded bg-card"></div>
                    <p className="font-medium">{bookFormats.kindle.label}</p>
                    <p className="text-sm text-amber-600 font-medium">{bookFormats.kindle.dimensions}</p>
                    <p className="text-xs text-muted-foreground mt-1">{bookFormats.kindle.description}</p>
                  </button>
                </div>

                {/* Editorial Estimate */}
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-1">Referência editorial:</p>
                  <p className="text-sm text-muted-foreground">
                    Média de <span className="font-medium text-foreground">{format.wordsPerPage} palavras por página</span>.
                    Um livro típico neste formato tem cerca de{' '}
                    <span className="font-medium text-foreground">
                      {format.avgPages} páginas ({format.avgWords.toLocaleString('pt-BR')} palavras)
                    </span>.
                  </p>
                </div>
              </div>

              {/* Cor da Página do Editor */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-3">Cor da página do editor</label>
                <p className="text-xs text-muted-foreground mb-3">
                  Escolha a cor de fundo do seu editor. Isso não afeta a exportação ou impressão.
                </p>
                <div className="flex gap-3">
                  {pageColors.map((color) => (
                    <button
                      key={color.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, editor_page_color: color.id })}
                      className={`
                        flex-1 p-3 rounded-lg border-2 transition-all
                        ${formData.editor_page_color === color.id
                          ? 'border-amber-500 ring-2 ring-amber-500/20'
                          : 'border-border hover:border-amber-500/50'
                        }
                      `}
                      data-testid={`page-color-${color.id}`}
                    >
                      <div 
                        className="w-full h-8 rounded mb-2 border border-border/50"
                        style={{ backgroundColor: color.color }}
                      >
                        <div 
                          className="h-full flex items-center justify-center text-xs"
                          style={{ color: color.textColor }}
                        >
                          Abc
                        </div>
                      </div>
                      <p className="text-sm font-medium">{color.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Capa do Livro with 3D Mockup */}
              <div>
                <label className="block text-sm font-medium mb-3">Capa do Livro</label>
                <div className="border-2 border-dashed border-border rounded-lg p-6 hover:border-amber-500/50 transition-colors">
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    
                    {/* Clean 3D Book Mockup */}
                    <div className="shrink-0" style={{ perspective: '1000px' }}>
                      <div 
                        className="relative transition-transform duration-500 hover:scale-105"
                        style={{ 
                          transformStyle: 'preserve-3d',
                          transform: 'rotateY(-15deg)',
                          width: '200px',
                          height: '290px'
                        }}
                      >
                        {/* Spine - Subtle dark */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '20px',
                            height: '100%',
                            background: 'linear-gradient(to right, #2a3a4a, #3a4a5a, #2a3a4a)',
                            transform: 'rotateY(90deg) translateX(-10px)',
                            transformOrigin: 'left center',
                            borderRadius: '2px 0 0 2px'
                          }}
                        />
                        
                        {/* Pages edge - Right side */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '3px',
                            right: '-8px',
                            width: '16px',
                            height: 'calc(100% - 6px)',
                            background: 'linear-gradient(to right, #f8f8f5, #ffffff)',
                            borderRadius: '0 1px 1px 0',
                            boxShadow: '1px 0 3px rgba(0,0,0,0.1)'
                          }}
                        />
                        
                        {/* Front Cover */}
                        <div 
                          style={{
                            position: 'absolute',
                            top: '0',
                            left: '0',
                            width: '100%',
                            height: '100%',
                            borderRadius: '0 3px 3px 0',
                            overflow: 'hidden',
                            boxShadow: '5px 5px 20px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.1)'
                          }}
                        >
                          {formData.cover_url ? (
                            <>
                              <img 
                                src={formData.cover_url} 
                                alt="Capa do livro" 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="hidden w-full h-full bg-stone-100 items-center justify-center">
                                <p className="text-xs text-stone-400">Erro ao carregar</p>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                              <p className="text-sm text-stone-400 text-center px-4">
                                Sua capa<br/>aparecerá aqui
                              </p>
                            </div>
                          )}
                          
                          {/* Subtle shine */}
                          <div 
                            style={{
                              position: 'absolute',
                              inset: '0',
                              pointerEvents: 'none',
                              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                              borderRadius: '0 3px 3px 0'
                            }}
                          />
                        </div>
                        
                        {/* Bottom shadow */}
                        <div 
                          style={{
                            position: 'absolute',
                            bottom: '-15px',
                            left: '10%',
                            width: '80%',
                            height: '15px',
                            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, transparent 70%)',
                            filter: 'blur(4px)'
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Upload Options */}
                    <div className="flex-1 space-y-4 w-full">
                      {/* URL Input */}
                      <div>
                        <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                          <LinkIcon size={14} />
                          URL da imagem
                        </label>
                        <Input
                          type="url"
                          value={formData.cover_url}
                          onChange={(e) => setFormData({ ...formData, cover_url: e.target.value })}
                          placeholder="https://exemplo.com/capa.jpg"
                          className="text-sm"
                          data-testid="cover-url-input"
                        />
                      </div>

                      {/* File Upload */}
                      <div>
                        <label className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                          <Upload size={14} />
                          Ou envie um arquivo
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              // Convert to base64 for preview (small files only)
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error('Arquivo muito grande. Máximo 5MB.');
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setFormData({ ...formData, cover_url: event.target.result });
                                toast.success('Capa carregada!');
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="block w-full text-sm text-muted-foreground
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-medium
                            file:bg-amber-500/10 file:text-amber-700
                            hover:file:bg-amber-500/20
                            cursor-pointer"
                          data-testid="cover-file-input"
                        />
                      </div>

                      {/* Clear button */}
                      {formData.cover_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, cover_url: '' })}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X size={14} className="mr-1" />
                          Remover capa
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center gap-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-stone-900 px-8"
                data-testid="create-book-button"
              >
                <BookOpen className="mr-2" size={20} />
                {loading ? (isEditMode ? 'Salvando...' : 'Criando...') : (isEditMode ? 'Salvar Alterações' : 'Criar Livro')}
              </Button>
              
              <button
                type="button"
                onClick={() => navigate(isEditMode ? `/livro/${bookId}` : '/meus-livros')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
